import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Paper, Box } from "@mui/material";
import Button from "../../../components/bootstrap/Button";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../../components/ToastComponent";
import ToastComponent from "../../../components/ToastComponent";
import FormGroup from "../../../components/bootstrap/forms/FormGroup";
import Select from "../../../components/bootstrap/forms/Select";
import { AgGridReact } from "ag-grid-react";
import { GridApi } from "ag-grid-community";
import * as ExcelJS from "exceljs";

function ReportGrid() {
  const vyu_api_url = process.env.REACT_APP_VYU_API_URL;
  const token: any = localStorage.getItem("token");
  const [widgets, setWidgets] = useState<any[]>([]);
  const [selectedWidgetId, setSelectedWidgetId] = useState("");
  const [selectedWidgetObject, setSelectedWidgetObject] = useState<any | null>(
    null
  );
  const [rows, setRows] = useState<any>(null);
  const [columns, setColumns] = useState<any>(null);
  const headers = new Headers({ Authorization: "Bearer " + token });
  const vyu_service_url = process.env.REACT_APP_VYU_SERVICE_URL;
  const gridApiRef = useRef<GridApi | null>(null);
  const gridColumnApiRef = useRef<any | null>(null);
  const selectedWidgetObjectRef = useRef<any | null>(null);

  type FormValues = {
    name: string;
    selectQuery: any;
    selectParent: any;
    selectViewType: any;
    createUser: string;
  };
  const form = useForm<FormValues>();

  // Toast handlers
  const handleWarning = (message: string) => showWarningToast(message);
  const handleSuccess = (message: string) => showSuccessToast(message);
  const handleError = (message: string) => showErrorToast(message);

  // Fetch widgets
  useEffect(() => {
    fetchAllTheWidget();
  }, []);
  useEffect(() => {
    selectedWidgetObjectRef.current = selectedWidgetObject;
  }, [selectedWidgetObject]);

  const fetchAllTheWidget = async () => {
    try {
      const response = await fetch(`${vyu_api_url}/widget/reportViewWidgets`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch data");

      const data = await response.json();
      if (data.obj && data.obj.length > 0) {
        setWidgets(data.obj);
      } else {
        handleWarning("No widgets are available to select.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newWidgetId = event.target.value;
    setSelectedWidgetId(newWidgetId);

    const foundWidget = widgets.find(
      (widget) => widget.id.toString() === newWidgetId
    );
    console.log("SelectedWidgetObject", foundWidget);

    setSelectedWidgetObject(foundWidget || null);
    if (foundWidget) handlePreview(foundWidget);
  };

  const getDataType = (value: any): "date" | "number" | "string" => {
    if (typeof value !== "string" || !value) {
      if (!isNaN(parseFloat(value)) && isFinite(value)) return "number";
      return "string";
    }
    // Check for 'yyyy-mm-dd' format
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return "date";
    }
    return "string";
  };

  const handlePreview = (foundWidget: any) => {
    const contextValue = "report";
    const queryId =
      typeof foundWidget?.queryId !== "number"
        ? foundWidget?.queryId?.id
        : foundWidget?.queryId;

    if (!queryId) {
      handleError("No valid queryId found for the selected widget.");
      return;
    }

    fetch(
      `${vyu_service_url}/getPreviewData?queryId=${queryId}&context=${contextValue}`,
      { headers: headers }
    )
      .then((response) => {
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (json?.obj?.length > 0) {
          const keys = Object.keys(json.obj[0]);
          const cols = keys.map((key) => ({
            field: key,
            headerName: key.toUpperCase(),
            valueFormatter: (params: any) => {
              const dateRegex =
                /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?Z?)?$/;
              if (
                typeof params.value === "string" &&
                dateRegex.test(params.value)
              ) {
                const date = new Date(params.value);
                if (!isNaN(date.getTime())) {
                  return date.toLocaleDateString("en-GB"); // 'dd/mm/yyyy' format
                }
              }
              return params.value;
            },
            filter: true,
            headerComponentParams: { suppressMenu: true },
          }));

          const rowsWithIds = json.obj.map((obj: any, index: number) => ({
            ...obj,
            id: index + 1,
          }));

          setColumns(cols);
          setRows(rowsWithIds);
        } else {
          handleWarning("No data to display for the selected report.");
          setRows([]);
          setColumns([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching preview data:", error);
        handleError("Failed to fetch report data.");
        setRows([]);
        setColumns([]);
      });
  };

  const handleClearFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
      gridApiRef.current.onFilterChanged();
      handleSuccess("Filters cleared successfully.");
    }
  };

  const handleExportClick = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const date = String(now.getDate()).padStart(2, "0");
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const amPm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;

    const formattedDate = `${year}-${month}-${date}_${String(hours).padStart(2, "0")}:${minutes} ${amPm}`;

    if (gridApiRef.current) {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet1");
      const currentWidget = selectedWidgetObjectRef.current;
      const widgetHeading =
        currentWidget?.name || currentWidget?.queryId?.name || "Widget Data";
      worksheet.mergeCells("A1:Z1");
      worksheet.getCell("A1").value = widgetHeading.toUpperCase();
      worksheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      worksheet.getCell("A1").font = { bold: true, size: 14 };

      const columnWidths = [20, 30, 15, 25];
      worksheet.columns = columnWidths.map((width) => ({ width }));

      const rowData: any = gridApiRef?.current
        ?.getDataAsCsv()
        ?.split("\n")
        .map((row: any) => row.split(","))
        .filter((row: any) => row.length > 0)
        .map((row: any) =>
          row.map((cell: any) => cell.replace(/"/g, "").trim())
        );
      worksheet.columns = rowData[0]?.map(() => ({
        width: 40,
      }));

      rowData.forEach((row: any, index: number) => {
        const worksheetRow = worksheet.addRow(row);

        if (index === 0) {
          worksheetRow.eachCell((cell: any) => {
            cell.font = { bold: true };
          });
        }
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${widgetHeading}_${formattedDate}.xlsx`;
      link.click();
    }
  };

  const onGridReady = (params: any) => {
    gridApiRef.current = params.api;
    gridColumnApiRef.current = params.columnApi;
  };

  const defaultColDef = { floatingFilter: true, flex: 1 };

  // Inject Export button at bottom
  useEffect(() => {
    const interval = setInterval(() => {
      const paginationPanel = document.querySelector(".ag-paging-panel");
      if (
        paginationPanel &&
        !document.getElementById("custom-pagination-btn")
      ) {
        const button = document.createElement("button");
        button.textContent = "Export";
        button.id = "custom-pagination-btn";
        button.style.marginRight = "10px";
        button.style.backgroundColor = "#0D57DE";
        button.style.borderColor = "#0D57DE";
        button.style.color = "white";
        button.style.borderRadius = "8px";
        button.style.padding = "8px 16px";
        button.style.fontWeight = "bold";
        button.onclick = () => handleExportClick();
        paginationPanel.appendChild(button);
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 150px)",
        gap: "1rem",
      }}
    >
      {/* Top Bar with Report Dropdown & Clear Filters */}
      <div
        className="col-12 mb-3"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <FormGroup>
          <Select
            style={{
              width: "250px",
              borderRadius: "8px",
              backgroundColor: "white",
              border: "1px solid #d0d5dd",
              padding: "6px 40px 6px 12px",
              fontSize: "14px",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
            ariaLabel="State"
            placeholder="Select a report..."
            id="report"
            name="report"
            value={selectedWidgetId}
            onChange={handleChange}
            title={
              selectedWidgetObject
                ? selectedWidgetObject.name
                : "Select a report..."
            }
          >
            <option value="" disabled>
              Select a report...
            </option>
            {widgets.map((widget) => (
              <option key={widget.id} value={widget.id}>
                {widget.name}
              </option>
            ))}
          </Select>
        </FormGroup>
        {selectedWidgetId && (
          <Button
            className="signin-button"
            type="submit"
            onClick={handleClearFilters}
            style={{ width: "7%", height: "90%" }}
          >
            <span style={{ fontSize: "12px", fontWeight: "bold" }}>
              Clear Filters
            </span>
          </Button>
        )}
      </div>

      <div style={{ height: "100vh", width: "100%" }}>
        {selectedWidgetObject && (
          <Paper
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box width={"100%"} height={"100%"} gap={"4px"}>
              <AgGridReact
                className="ag-theme-quartz cancelSelectorName"
                rowData={rows}
                columnDefs={columns}
                pagination={true}
                paginationPageSize={100}
                onGridReady={onGridReady}
                defaultColDef={defaultColDef}
              />
            </Box>
          </Paper>
        )}
      </div>

      <ToastComponent />
    </div>
  );
}

export default ReportGrid;
